import React, { useState } from "react";

import HttpManager from "../../services/HttpManager";
import ProductPicker from "../../navigation/v2/store/products/productPicker";
import { FIELD_FONT } from "./kitUi";

/*
	Ventas del catálogo viejo (Esteban, 2026-10-29, cierre de los módulos viejos): muchas se entregaron por partes sin
	marcarse. Al entregar se escribe lo que se le dio antes (no descuenta) y se agregan los productos del catálogo nuevo que
	se lleva hoy (buscados, con talla y color), que sí salen de la tienda. Completa o parcial; firma y foto como siempre.
*/

export interface LegacyLine { articleId: number; name: string; quantity: string; onHand: number | null }
export interface LegacyDraft { type: "total" | "partial"; previous: string; lines: LegacyLine[] }

export const emptyLegacy = (): LegacyDraft => ({ type: "total", previous: "", lines: [] });

const qtyOk = (line: LegacyLine): boolean => /^\d+$/.test(line.quantity) && Number(line.quantity) >= 1 && (line.onHand === null || Number(line.quantity) <= line.onHand);

/** Something was handed out (today's products or what was given before) and every quantity fits the stock. */
export const legacyValid = (draft: LegacyDraft, note = ""): boolean =>
	draft.lines.every(qtyOk) && (draft.lines.length > 0 || draft.previous.trim().length >= 3 || note.trim().length >= 3);

export const legacyItems = (draft: LegacyDraft) => draft.lines.map((line) => ({ articleId: line.articleId, quantity: Number(line.quantity) }));

const radio = (selected: boolean, onPick: () => void, title: string, detail: string, busy?: boolean) => <div role="radio" aria-checked={selected}
	onClick={() => { if (!busy) onPick(); }}
	style={{ flex: "1 1 200px", border: "1px solid " + (selected ? "var(--wa-acento)" : "var(--wa-filo)"), background: selected ? "var(--wa-acento-suave)" : "var(--wa-superficie)", borderRadius: "8px", padding: "8px 10px", cursor: busy ? "default" : "pointer" }}>
	<div style={{ fontWeight: 600, fontSize: "14px" }}>{title}</div>
	<div style={{ fontSize: "12px", color: "var(--wa-apagado)" }}>{detail}</div>
</div>;

const caption: React.CSSProperties = { fontWeight: 600, fontSize: "13px", marginTop: "10px", marginBottom: "4px" };

const EntregaVentaVieja: React.FC<{
	campusId: number; warehouse: { id: number; name: string } | null; value: LegacyDraft; onChange: (draft: LegacyDraft) => void; busy?: boolean;
}> = ({ campusId, warehouse, value, onChange, busy }) => {
	/** Product with sizes/colors just picked: choose which one. */
	const [choosing, setChoosing] = useState<{ name: string; variants: { id: number; label: string; onHand: number }[] } | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const onHandHere = (levels: any[] | undefined): number | null => {
		if (!warehouse) return null;
		const level = (levels || []).find((entry: any) => entry.warehouseId === warehouse.id);
		return level ? Number(level.onHand) : 0;
	};
	const add = (line: LegacyLine) => {
		const existing = value.lines.find((entry) => entry.articleId === line.articleId);
		const lines = existing
			? value.lines.map((entry) => entry.articleId === line.articleId ? { ...entry, quantity: String((Number(entry.quantity) || 0) + 1) } : entry)
			: [...value.lines, line];
		onChange({ ...value, lines });
		setChoosing(null);
	};
	const pick = async (product: any) => {
		setError(null);
		setLoading(true);
		try {
			const response = await HttpManager.getInstance().inventoryService.get(campusId, product.id);
			const detail = response.data;
			const variants = (detail.variants || []).filter((variant: any) => !variant.deprecatedAt);
			if (variants.length > 0) {
				setChoosing({ name: detail.name, variants: variants.map((variant: any) => ({ id: variant.id, label: variant.label || [variant.colorValue, variant.sizeValue].filter(Boolean).join(" · "), onHand: onHandHere(variant.stockByWarehouse) ?? 0 })) });
				return;
			}
			if (!detail.trackStock) { setError("\"" + detail.name + "\" no lleva existencia."); return; }
			add({ articleId: detail.id, name: detail.name, quantity: "1", onHand: onHandHere(detail.stockByWarehouse) });
		} catch {
			setError("No se pudo abrir el producto.");
		} finally {
			setLoading(false);
		}
	};
	const setQty = (articleId: number, text: string) => onChange({ ...value, lines: value.lines.map((line) => line.articleId === articleId ? { ...line, quantity: text.replace(/\D/g, "").slice(0, 3) } : line) });

	return <div>
		<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px" }}>
			{radio(value.type === "total", () => onChange({ ...value, type: "total" }), "Entrega Completa", "Con esto ya tiene todo lo que compró.", busy)}
			{radio(value.type === "partial", () => onChange({ ...value, type: "partial" }), "Entrega Parcial", "Todavía le falta algo por entregar.", busy)}
		</div>

		<div style={caption}>Lo que se le entregó antes <span style={{ fontWeight: 400, color: "var(--wa-apagado)" }}>(no descuenta inventario)</span></div>
		<textarea className="form-control" rows={2} disabled={busy} value={value.previous} maxLength={2000}
			placeholder="Ej.: En septiembre se le dio el maletín, las tijeras y la filipina."
			onChange={(event) => onChange({ ...value, previous: event.target.value })} style={{ resize: "vertical", fontSize: FIELD_FONT }} />

		<div style={caption}>Lo que se lleva hoy <span style={{ fontWeight: 400, color: "var(--wa-apagado)" }}>(sale de {warehouse ? warehouse.name : "la tienda"})</span></div>
		{value.lines.map((line) => <div key={line.articleId} style={{ display: "flex", gap: "8px", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--wa-filo)" }}>
			<div style={{ flex: "1 1 auto", minWidth: 0 }}>
				<div style={{ fontWeight: 600, fontSize: "14px" }}>{line.name}</div>
				{line.onHand !== null ? <div style={{ fontSize: "12px", color: line.onHand < 1 ? "var(--wa-oro)" : "var(--wa-apagado)" }}>hay {line.onHand}</div> : null}
			</div>
			<input type="text" inputMode="numeric" pattern="[0-9]*" className="form-control" aria-label="Cantidad" disabled={busy} value={line.quantity}
				onChange={(event) => setQty(line.articleId, event.target.value)}
				style={{ width: "70px", height: "34px", fontSize: FIELD_FONT, borderColor: qtyOk(line) ? undefined : "var(--wa-oro)" }} />
			<button type="button" className="btn btn-sm btn-link" style={{ color: "var(--wa-apagado)", padding: 0 }} disabled={busy} aria-label="Quitar"
				onClick={() => onChange({ ...value, lines: value.lines.filter((entry) => entry.articleId !== line.articleId) })}>✕</button>
		</div>)}
		{value.lines.some((line) => line.onHand !== null && Number(line.quantity) > line.onHand)
			? <div style={{ fontSize: "12px", color: "var(--wa-oro)", marginTop: "4px" }}>No alcanza la existencia de la tienda para lo marcado.</div> : null}
		{choosing ? <div style={{ border: "1px solid var(--wa-filo)", borderRadius: "8px", padding: "8px 10px", marginTop: "6px" }}>
			<div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
				<span style={{ fontWeight: 600, fontSize: "14px" }}>{choosing.name}: ¿Qué talla o color?</span>
				<button type="button" className="btn-close" style={{ fontSize: "10px" }} disabled={busy} onClick={() => setChoosing(null)} />
			</div>
			<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "6px" }}>
				{choosing.variants.map((variant) => <button key={variant.id} type="button" disabled={busy || variant.onHand < 1}
					className={"btn btn-sm rounded-pill " + (variant.onHand < 1 ? "btn-outline-secondary" : "btn-outline-primary")}
					onClick={() => add({ articleId: variant.id, name: choosing.name + " · " + variant.label, quantity: "1", onHand: variant.onHand })}>
					{variant.label} <span style={{ opacity: 0.7 }}>· {variant.onHand}</span>
				</button>)}
			</div>
		</div> : <div style={{ marginTop: "6px" }}>
			<ProductPicker campusId={campusId} kinds={["article", "vitrina"]} excludeIds={value.lines.map((line) => line.articleId)} disabled={busy || loading}
				groupVariants placeholder={loading ? "Abriendo…" : "Buscar producto que se lleva hoy…"} onPick={pick} />
		</div>}
		{error ? <div style={{ fontSize: "12px", color: "var(--wa-oro)", marginTop: "4px" }}>{error}</div> : null}
	</div>;
};

export default EntregaVentaVieja;
